import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Drawer,
  Button,
  TextField,
  Divider,
  Fade,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Card,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  Share,
  VolumeUp,
  VolumeOff,
  Close,
  Verified,
  Send,
  MoreVert,
  BookmarkBorder,
  Bookmark,
  Pause,
  ThumbUp,
  ThumbDown,
  LocationOn,
  CheckCircle,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ErrorOutline,
  Visibility,
  Description,
  ThumbDownOutlined,
  DescriptionOutlined,
  ThumbUpOutlined,
  Download as DownloadIcon,
} from '@mui/icons-material';
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper';
import BreadcrumbHeader from 'layout/BreadcrumbsHeader';
import styled from '@emotion/styled';
import Cookies from 'js-cookie';
import { usePitchReels } from 'hooks/usePitchReels';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PitchReelsFeedSkeleton } from '../PitchReelsFeedSkeleton';
import NoData from 'ui-components/NoData';
import placeHolderImageNew from 'assets/no_data_images/Startups/new_startups.svg'
import CustomPopup from 'ui-components/popups/CustomPopup';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { checkForAuthorization, downloadFile, getReelLastViewedAt, hasPermission, isVideoUrl, setReelLastViewedAt, shouldRecordView } from 'utils/index';
import { SaveView } from 'api/startup_applications';
import { useWindowSize } from 'ui-components/hooks';
import { Document, Page } from 'react-pdf';
import { useSelector } from 'react-redux';
import { useToast } from 'context/ToastContext';
import axios from 'axios';
import { QueryClient } from '@tanstack/react-query';
import { Primary, Secondary } from 'ui-components/Buttons';
import { trackEngagementEvent, VIEW_TYPES } from 'api/engagement';

// Seconds of actual playback before a reel counts as viewed, so scrolling past
// a reel does not record a view. Reels shorter than this count once they finish.
const REEL_VIEW_DWELL_SECONDS = 3;

export const DisplayDocuments = ({ type, url }) => {
  const theme = useTheme();
  const width = useWindowSize();
  const [numPages, setNumPages] = React.useState(1);
  const [pageWidth, setPageWidth] = React.useState(0);

  React.useEffect(() => {
    setPageWidth(document.getElementById('pdf_container')?.parentNode.clientWidth)
  }, [width])

  // console.log(type, isVideoUrl(type), isDocumentOrExcelUrl(type))
  if (type === "pdf") {
    return <Document error={() => { return <NoData /> }} loading={<Box className="skeleton" height={"calc(100vh - 160px)"} width={"100%"}></Box>} file={url} onLoadSuccess={({ numPages }) => { setNumPages(numPages) }}>
      <div id='pdf_container3' style={{ backgroundColor: theme.palette.customColors.gray300, width: '100%' }}>
        {Array.apply(null, Array(numPages))
          .map((x, i) => i + 1)
          .map((page, index) => (
            <div id='page_view' style={{ marginBottom: index != numPages - 1 ? '0.7rem' : 0 }}>
              <Page
                key={page}
                pageNumber={page}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                scale={1}
                // width={"100%"}
                loading={<Box className="skeleton" height={"calc(100vh - 160px)"} width={"100%"}></Box>}
                width={pageWidth > 0 ? pageWidth : document.getElementById('pdf_container3')?.parentNode.clientWidth}
              />
            </div>
          ))}
      </div>
    </Document>
  }
  else if (isVideoUrl(type)) {
    return <video src={url} width={"100%"} controls style={{
      borderRadius: '8px',
    }} ></video>
  }
  else {
    return <DocViewer documents={[{ uri: url, fileType: type }]} pluginRenderers={DocViewerRenderers} style={{ height: "100vh", minWidth: "100%", borderRadius: "8px" }} config={{ header: { disableHeader: true } }} />
  }
}

const RichTextDisplay = ({ htmlContent, color = '' }) => {
  const [open, setOpen] = React.useState(false);
  const content = open ? htmlContent : htmlContent?.substring(0, 300);

  return (
    <>
      {content && content?.length > 0 ? (
        <>
          <div
            dangerouslySetInnerHTML={{ __html: content }}
            className="row rich-text"
            style={{ color, textAlign: 'justify' }}
          />
          {htmlContent?.length > 300 && (
            <Secondary
              component="label"
              role={undefined}
              tabIndex={-1}
              sx={{
                backgroundColor: 'transparent',
                border: '0',
                padding: '0',
              }}
              onClick={() => setOpen(!open)}
            >
              <div>{!open ? 'See more' : 'See less'}</div>
            </Secondary>
          )}
        </>
      ) : (
        '-'
      )}
    </>
  );
};

// Reel action buttons (Shortlist / Reject / Deck), per the Figma: a white circle with a
// 1px brand-gradient border, a gradient icon and a plain label underneath.
const REEL_ICON_GRADIENT_ID = 'reel_action_gradient';
const REEL_GRADIENT = 'linear-gradient(180deg, #7852F8 0%, #84AEF6 100%)';
// A gradient border needs two backgrounds: the white fill clipped to the padding box,
// the gradient clipped to the border box showing through the transparent 1px border.
// Icon circle sized down towards the Figma, where the glyph reads small against its
// label rather than dominating it.
const reelActionButtonSx = {
  width: 48,
  height: 48,
  padding: 0,
  borderRadius: '50%',
  border: '1px solid transparent',
  background: `linear-gradient(#fff, #fff) padding-box, ${REEL_GRADIENT} border-box`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `linear-gradient(#fff, #fff) padding-box, ${REEL_GRADIENT} border-box`,
    transform: 'scale(1.08)',
  },
  '&:active': { transform: 'scale(0.92)' },
};
const reelActionIconSx = { fontSize: 20, fill: `url(#${REEL_ICON_GRADIENT_ID})` };
const reelActionLabelSx = {
  fontSize: '0.7rem',
  fontWeight: '600 !important',
  lineHeight: 1,
  // Dark on desktop (labels sit on the white page), white below sm where the reel is
  // full-screen on a black background - otherwise #344054 is invisible on mobile.
  color: { xs: '#fff', sm: '#344054' },
  width: 'max-content',
  // No margins: each icon now sits in its own reelActionItemSx group with its label,
  // so the icon-to-label distance is set there. The old marginTop stacked on top of
  // the container's gap, which is what pushed the label away from its icon.
  cursor: 'pointer',
};

// One action = icon button with its label directly beneath. The Figma keeps these
// tight and spaces the actions apart; the container's gap used to do both jobs.
const reelActionItemSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25rem',
};

// UPDATED ActionButtons component with new text labels
const ActionButtons = ({
  isLargeScreen,
  currentIndex,
  onPreviousReel,
  activeButtons,
  setActiveButtons,
  onInterested,
  onUninterested,
  onOpenDocument,
  onNextReel,
  isVisible = true,
  isSelected,
  isActionLoading = false,
  hasPitchDeck = true,
  userPermissions,
  addToast,
  onMuteVideo,
  // The Deal Flow overlay uses the mobile action styling at every width; the
  // Reels page leaves this false and keeps its breakpoint behaviour.
  forceMobile = false
}) => {
  const theme = useTheme();
  // Breakpoint matches the rail's own position, which is `right: { xs: 8, md:
  // '-5.5rem' }` - with no `sm` key that resolves to 8 (inside the card) all the
  // way up to md. Switching the style at `sm` left the 600-900px band drawing the
  // desktop white/gradient circles ON the video, where the #344054 labels are
  // unreadable. Inside the card => mobile treatment.
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || forceMobile;
  if (!isVisible || isSelected) return null;

  const handleLikeClick = (e) => {
    e.stopPropagation();
    if (isActionLoading) return;
    if (!hasPermission("pitch_reels", "edit", userPermissions)) {
      addToast("Unable to perform this action - Contact Owner", 'error');
      return
    };
    setActiveButtons({ interested: true, uninterested: false });
    onInterested();
  };

  const handlePassClick = (e) => {
    e.stopPropagation();
    if (isActionLoading) return;
    if (!hasPermission("pitch_reels", "edit", userPermissions)) {
      addToast("Unable to perform this action - Contact Owner", 'error');
      return
    };
    setActiveButtons({ interested: false, uninterested: true });
    onUninterested();
  };

  const handleOpenDocument = (e) => {
    e.stopPropagation();
    onMuteVideo();
    onOpenDocument();
  };

  // Mobile keeps the PREVIOUS (production) action-button look — plain white glowing
  // icons, no gradient circle. Desktop/tablet (sm+) use the new Figma white-circle
  // style in the return below. Team asked to revert only mobile, nothing else.
  if (isMobile) {
    const iconGlowStyle = {
      filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.8)) drop-shadow(0 0 12px rgba(0,0,0,0.6)) drop-shadow(0 0 16px rgba(0,0,0,0.4))',
    };
    const textGlowStyle = {
      textShadow: '0 0 4px rgba(0,0,0,0.6), 0 0 8px rgba(0,0,0,0.4), 0 0 12px rgba(0,0,0,0.2), 0 0 16px rgba(0,0,0,0)',
    };
    const mobileBtnSx = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      px: 1.5,
      py: 1.5,
      borderRadius: '50%',
      backgroundColor: 'transparent',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': { backgroundColor: 'transparent', transform: 'scale(1.08)' },
      '&:active': { transform: 'scale(0.92)' },
    };
    const mobileLabelSx = {
      fontSize: '0.7rem',
      color: '#fff',
      fontWeight: '700 !important',
      lineHeight: 1,
      background: 'transparent',
      padding: '4px 8px',
      borderRadius: '8px',
      width: 'max-content',
      marginTop: '-0.5rem',
      marginBottom: '0.5rem',
      ...textGlowStyle,
    };
    return (
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          // Sits just above the startup info row (logo / name / View Profile).
          // The info row's height comes from absolute font sizes, so on the Deal
          // Flow overlay's shorter card it covers a bigger share and 16% puts the
          // Deck label underneath it - hence the higher offset there.
          bottom: forceMobile ? '25%' : '16%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 10,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s ease',
          pointerEvents: isVisible ? 'auto' : 'none',
          animation: isVisible ? 'fadeInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          '@keyframes fadeInRight': {
            '0%': { opacity: 0, transform: 'translateX(30px)' },
            '100%': { opacity: 1, transform: 'translateX(0)' },
          },
        }}
      >
        {/* Shortlist Button */}
        <IconButton
          onClick={handleLikeClick}
          sx={{ ...mobileBtnSx, cursor: isActionLoading ? 'default' : 'pointer', opacity: isActionLoading ? 0.7 : 1, '&:hover': { backgroundColor: 'transparent', transform: isActionLoading ? 'none' : 'scale(1.08)' }, '&:active': { transform: isActionLoading ? 'none' : 'scale(0.92)' } }}
        >
          {isActionLoading && activeButtons.interested ? (
            <CircularProgress size={20} sx={{ color: '#fff', ...iconGlowStyle }} />
          ) : activeButtons.interested ? (
            <ThumbUp sx={{ fontSize: 22, color: '#fff', ...iconGlowStyle }} />
          ) : (
            <ThumbUpOutlined sx={{ fontSize: 22, color: '#fff', ...iconGlowStyle }} />
          )}
        </IconButton>
        <Typography onClick={handleLikeClick} sx={{ ...mobileLabelSx, cursor: isActionLoading ? 'default' : 'pointer' }}>
          Shortlist
        </Typography>

        {/* Reject Button */}
        <IconButton
          onClick={handlePassClick}
          sx={{ ...mobileBtnSx, cursor: isActionLoading ? 'default' : 'pointer', opacity: isActionLoading ? 0.7 : 1, '&:hover': { backgroundColor: 'transparent', transform: isActionLoading ? 'none' : 'scale(1.08)' }, '&:active': { transform: isActionLoading ? 'none' : 'scale(0.92)' } }}
        >
          {isActionLoading && activeButtons.uninterested ? (
            <CircularProgress size={20} sx={{ color: '#fff', ...iconGlowStyle }} />
          ) : activeButtons.uninterested ? (
            <ThumbDown sx={{ fontSize: 22, color: '#fff', ...iconGlowStyle }} />
          ) : (
            <ThumbDownOutlined sx={{ fontSize: 22, color: '#fff', ...iconGlowStyle }} />
          )}
        </IconButton>
        <Typography onClick={handlePassClick} sx={{ ...mobileLabelSx, cursor: isActionLoading ? 'default' : 'pointer' }}>
          Reject
        </Typography>

        {/* View Deck Button */}
        <IconButton onClick={(e) => { hasPitchDeck && handleOpenDocument(e); }} sx={mobileBtnSx}>
          <DescriptionOutlined sx={{ fontSize: 22, color: '#fff', ...iconGlowStyle }} />
        </IconButton>
        <Typography sx={mobileLabelSx} onClick={(e) => { hasPitchDeck && handleOpenDocument(e); }}>
          {hasPitchDeck ? "Deck" : "Unavailable"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        right: { xs: 8, md: "-5.5rem" },
        bottom: { xs: '25%', md: "5%" },
        display: 'flex',
        flexDirection: 'column',
        alignItems: "center",
        // 8px on mobile, 20px from md - both measured in the browser. Now that each icon
        // sits in its own group with its label (reelActionItemSx), this gap only separates
        // the actions - it no longer also controls the icon-to-label distance.
        gap: { xs: 1, md: 2.5 },
        zIndex: 10,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s ease',
        pointerEvents: isVisible ? 'auto' : 'none',
        animation: isVisible ? 'fadeInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        '@keyframes fadeInRight': {
          '0%': {
            opacity: 0,
            transform: 'translateX(30px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      {/* Brand gradient for the action icons — one def, referenced via fill: url(#id) */}
      <Box component="svg" width={0} height={0} sx={{ position: 'absolute' }} aria-hidden>
        <defs>
          <linearGradient id={REEL_ICON_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7852F8" />
            <stop offset="1" stopColor="#84AEF6" />
          </linearGradient>
        </defs>
      </Box>

      {/* Shortlist Button */}
      <Box sx={reelActionItemSx}>
        <IconButton
          onClick={handleLikeClick}
          sx={{
            ...reelActionButtonSx,
            cursor: isActionLoading ? 'default' : 'pointer',
            opacity: isActionLoading ? 0.7 : 1,
            '&:hover': { ...reelActionButtonSx['&:hover'], transform: isActionLoading ? 'none' : 'scale(1.08)' },
            '&:active': { transform: isActionLoading ? 'none' : 'scale(0.92)' },
          }}
        >
          {isActionLoading && activeButtons.interested ? (
            <CircularProgress size={18} sx={{ color: '#7852F8' }} />
          ) : activeButtons.interested ? (
            <ThumbUp sx={reelActionIconSx} />
          ) : (
            <ThumbUpOutlined sx={reelActionIconSx} />
          )}
        </IconButton>
        <Typography
          onClick={handleLikeClick}
          sx={{ ...reelActionLabelSx, cursor: isActionLoading ? 'default' : 'pointer' }}
        >
          Shortlist
        </Typography>
      </Box>

      {/* Reject Button */}
      <Box sx={reelActionItemSx}>
        <IconButton
          onClick={handlePassClick}
          sx={{
            ...reelActionButtonSx,
            cursor: isActionLoading ? 'default' : 'pointer',
            opacity: isActionLoading ? 0.7 : 1,
            '&:hover': { ...reelActionButtonSx['&:hover'], transform: isActionLoading ? 'none' : 'scale(1.08)' },
            '&:active': { transform: isActionLoading ? 'none' : 'scale(0.92)' },
          }}
        >
          {isActionLoading && activeButtons.uninterested ? (
            <CircularProgress size={18} sx={{ color: '#7852F8' }} />
          ) : activeButtons.uninterested ? (
            <ThumbDown sx={reelActionIconSx} />
          ) : (
            <ThumbDownOutlined sx={reelActionIconSx} />
          )}
        </IconButton>
        <Typography
          onClick={handlePassClick}
          sx={{ ...reelActionLabelSx, cursor: isActionLoading ? 'default' : 'pointer' }}
        >
          Reject
        </Typography>
      </Box>

      {/* View Deck Button */}
      <Box sx={reelActionItemSx}>
        <IconButton
          onClick={(e) => {
            hasPitchDeck && handleOpenDocument(e)
          }}
          sx={reelActionButtonSx}
        >
          <DescriptionOutlined sx={reelActionIconSx} />
        </IconButton>
        <Typography
          sx={reelActionLabelSx}
          onClick={(e) => {
            hasPitchDeck && handleOpenDocument(e)
          }}
        >
          {hasPitchDeck ? "Deck" : "Unavailable"}
        </Typography>
      </Box>
    </Box>
  );
};


const ScrollButtons = ({
  isLargeScreen,
  currentIndex,
  onPreviousReel,
  onNextReel,
  isVisible = true,
  isSelected,
  isActionLoading = false,
}) => {
  if (!isVisible || isSelected) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        right: { xs: 8, sm: 12 },
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        zIndex: 10,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      {isLargeScreen && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onPreviousReel();
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1.5,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: { xs: undefined, md: 'blur(8px)' },
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.75)',
              transform: 'scale(1.05)',
            },
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <KeyboardArrowUp sx={{ fontSize: 22, color: '#fff' }} />
        </IconButton>
      )}

      {isLargeScreen && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onNextReel();
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 1.5,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: { xs: undefined, md: 'blur(8px)' },
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.75)',
              transform: 'scale(1.05)',
            },
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <KeyboardArrowDown sx={{ fontSize: 22, color: '#fff' }} />
        </IconButton>
      )}
    </Box>
  );
};

// EndOfFeedCard component - UPDATED for requirement #7
const EndOfFeedCard = React.forwardRef(({
  currentRoute,
  onContinue,
  onGoBack,
  noVideosAvailable,
  hasNextPage = false  // Add this prop
}, ref) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const getMessage = () => {
    switch (currentRoute) {
      case 'selected':
        return {
          title: "That's all for now",
          description: "You've seen everything from this startup",
          actionText: 'Explore Deal Flow',
          emoji: '🎯',
          actionClick: () => {
            setLoading(true);

            setTimeout(() => {
              onContinue()
              setLoading(false);
            }, 1000)
          }
        };
      case 'deal-flow':
        return {
          title: "You're all caught up!",
          description: 'No new matches in your preferences',
          actionText: 'Explore New Categories',
          emoji: '✨',
          goBackText: "Not Now",
          actionClick: () => {
            setLoading(true);

            setTimeout(() => {
              onContinue()
              setLoading(false);
            }, 1000)
          }
        };
      case 'preference':
        return {
          title: 'End of preference matches',
          description: 'Want to expand your search?',
          actionText: 'Expand Search',
          emoji: '🔍',
          actionClick: () => {
            setLoading(true);

            setTimeout(() => {
              onContinue()
              setLoading(false);
            }, 1000)
          }
        };
      case 'all':
        return {
          title: 'All caught up!',
          description: "You've viewed every reel available",
          actionText: 'Explore More',
          emoji: '🔍',
          goBackText: "Go Back",
          actionClick: () => {
            setLoading(true);

            setTimeout(() => {
              onContinue()
              setLoading(false);
            }, 2500)
          }
        };
      default:
        return {
          title: 'Nothing more here',
          description: 'Check back later for new content',
          actionText: 'Continue',
          emoji: '👋',
          actionClick: () => {
            setLoading(true);

            setTimeout(() => {
              onContinue()
              setLoading(false);
            }, 1000)
          }
        };
    }
  };

  const message = getMessage();

  return (
    <Box
      ref={ref}
      sx={{
        width: { xs: '100%', sm: "auto" },
        maxWidth: "100%",
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        // overflow: 'hidden',
        bgcolor: '#000',
        aspectRatio: '9 / 16',
        marginInline: "auto",
        marginBottom: "1rem",
        borderRadius: { xs: 0, sm: '16px' },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: 250, sm: 350 },
          px: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            fontSize: { xs: 72, sm: 80 },
            mb: 2,
            animation: 'bounce 2s ease-in-out infinite',
            '@keyframes bounce': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-10px)' },
            },
          }}
        >
          {message.emoji}
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            color: '#fff',
            fontSize: { xs: '1.25rem', sm: '1.75rem' },
            lineHeight: 1.2,
          }}
        >
          {message.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            mb: 4,
            fontSize: { xs: "0.75rem", sm: '1rem' },
            lineHeight: 1.6,
          }}
        >
          {message.description}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Primary
            size="small"
            onClick={() => {
              message?.actionClick();
            }}
            sx={{
              padding: "0.625rem 1rem",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: '1rem' },
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : message.actionText}
          </Primary>

          {onGoBack && (
            <Secondary
              size="small"
              onClick={() => onGoBack(currentRoute, noVideosAvailable)}
              sx={{
                padding: "0.625rem 1rem",
                fontWeight: 500,
                fontSize: { xs: "0.75rem", sm: '1rem' },
                transition: 'all 0.2s ease',
              }}
            >
              {message?.goBackText ? message?.goBackText : "Go Back"}
            </Secondary>
          )}
        </Box>
      </Box>
    </Box>
  );
}
);

// UPDATED VideoItem component with new metadata display and View Profile button
// Exported so the Deal Flow reel overlay (ui-components/ReelModal) renders the
// exact same player as this page - styling, action buttons and mobile behaviour.
export const VideoItem = React.memo(({
  video,
  isActive,
  onLike,
  onSave,
  onFollow,
  isLiked,
  isSaved,
  isFollowing,
  onOpenComments,
  onOpenProfile,
  onShare,
  isMuted,
  onToggleMute,
  onMuteVideo,
  isDesktop,
  onInterested,
  onUninterested,
  isInterested,
  onPreviousReel,
  onNextReel,
  currentIndex,
  totalVideos,
  isEndCardVisible,
  isSelected = false,
  isActionLoading = false,
  activeButtonsState = { interested: false, uninterested: false },
  setActiveButtonsState,
  userPermissions,
  addToast,
  handleOpenDocument,
  onReelWatched,
  // Deal Flow overlay: use the mobile action rail at every width. It lives inside
  // the video frame, so the desktop copy below is skipped to avoid a second set.
  forceMobileActions = false
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const videoRef = useRef(null);
  const hasRecordedViewRef = useRef(false);
  const onReelWatchedRef = useRef(onReelWatched);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showDoubleTapHeart, setShowDoubleTapHeart] = useState(false);
  const [doubleTapPosition, setDoubleTapPosition] = useState({ x: 0, y: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const lastTapRef = useRef(0);
  const tapTimeoutRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [isHoldingPause, setIsHoldingPause] = useState(false);
  const holdTimeoutRef = useRef(null);
  const wasPlayingBeforeHoldRef = useRef(false);
  const [videoError, setVideoError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const MAX_RETRIES = 3;

  const handlePressStart = useCallback((e) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }

    wasPlayingBeforeHoldRef.current = !videoEl.paused;

    holdTimeoutRef.current = setTimeout(() => {
      if (wasPlayingBeforeHoldRef.current) {
        videoEl.pause();
        setIsHoldingPause(true);
        setIsPaused(false);
      }
    }, 200);
  }, []);

  const handlePressEnd = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (isHoldingPause && wasPlayingBeforeHoldRef.current) {
      videoEl.play();
      setIsHoldingPause(false);
    }

    wasPlayingBeforeHoldRef.current = false;
  }, [isHoldingPause]);

  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (isActive && !isPaused) {
      setBuffering(true);
      videoEl.currentTime = 0;
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setBuffering(false);
            resetControlsTimeout();
          })
          .catch(() => setBuffering(false));
      }
    } else {
      videoEl.pause();
    }
  }, [isActive, resetControlsTimeout]);

  // Keep the latest callback reachable from the listeners registered once below.
  useEffect(() => { onReelWatchedRef.current = onReelWatched; }, [onReelWatched]);
  useEffect(() => { hasRecordedViewRef.current = false; }, [video?.id]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const recordViewIfWatched = (playedSeconds) => {
      if (hasRecordedViewRef.current) return;
      // A reel shorter than the dwell threshold can never reach it, so let it
      // qualify by playing through instead. duration is NaN until metadata loads.
      const threshold = videoEl.duration && videoEl.duration < REEL_VIEW_DWELL_SECONDS
        ? videoEl.duration
        : REEL_VIEW_DWELL_SECONDS;
      if (playedSeconds < threshold) return;
      hasRecordedViewRef.current = true;
      onReelWatchedRef.current?.(video);
    };

    const handleTimeUpdate = () => {
      setProgress(videoEl.currentTime);
      recordViewIfWatched(videoEl.currentTime);
    };
    const handleLoadedMetadata = () => {
      setDuration(videoEl.duration);
      setBuffering(false);
    };
    const handleWaiting = () => setBuffering(true);
    const handleCanPlay = () => setBuffering(false);
    const handleEnded = () => {
      recordViewIfWatched(Infinity);
      videoEl.currentTime = 0;
      videoEl.play();
    };

    videoEl.addEventListener('timeupdate', handleTimeUpdate);
    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('waiting', handleWaiting);
    videoEl.addEventListener('canplay', handleCanPlay);
    videoEl.addEventListener('ended', handleEnded);

    return () => {
      videoEl.removeEventListener('timeupdate', handleTimeUpdate);
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('waiting', handleWaiting);
      videoEl.removeEventListener('canplay', handleCanPlay);
      videoEl.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (isPaused) {
      videoEl.play();
      setIsPaused(false);
    } else {
      videoEl.pause();
      setIsPaused(true);
    }
    resetControlsTimeout();
  }, [isPaused, resetControlsTimeout]);

  const handleDoubleTapLike = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setDoubleTapPosition({ x, y });

      if (!isLiked) {
        onLike();
      }
      setShowDoubleTapHeart(true);
      setTimeout(() => setShowDoubleTapHeart(false), 1000);
    },
    [isLiked, onLike]
  );

  const handleVideoClick = useCallback(
    (e) => {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapRef.current;

      if (timeSinceLastTap < 300) {
        // double tap
        // handleDoubleTapLike(e);
      } else {
        // single tap
        togglePlayPause();
      }

      lastTapRef.current = now;
    },
    [togglePlayPause, handleDoubleTapLike]
  );

  useEffect(() => {
    setVideoError(false);
    setRetryCount(0);
  }, [video.videoUrl]);

  const handleVideoError = useCallback(
    (e) => {
      const videoEl = videoRef.current;
      if (!videoEl) return;

      const error = videoEl.error;
      console.error('Video Error:', {
        code: error?.code,
        message: error?.message,
        url: video.videoUrl,
      });

      setBuffering(false);
      setVideoError(true);

      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          setVideoError(false);
          videoEl.load();
        }, 2000);
      }
    },
    [retryCount, video.videoUrl]
  );

  useEffect(() => {
    // Reset description expansion when video changes
    setIsDescriptionExpanded(false);
  }, [isActive]);

  useEffect(() => {
    // Handle click outside description to collapse it
    const handleClickOutside = (event) => {
      if (descriptionRef.current && !descriptionRef.current.contains(event.target)) {
        event.stopPropagation();
        setIsDescriptionExpanded(false);
      }
    };

    if (isDescriptionExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDescriptionExpanded]);

  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          width: { xs: '100%', sm: 'calc(100% - 2rem)' },
          height: {
            xs: 'calc(100dvh - 5rem)',
            sm: 'calc(100dvh - 6rem)',
            md: 'calc(100dvh - 7rem)',
          },
          scrollSnapAlign: 'center',
          scrollSnapStop: 'always',
          scrollMarginTop: '0px',
          position: 'relative',
          p: { sm: '1rem' },
          pb: '1rem !important',
          background: { xs: '#000', sm: 'transparent' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease-out',
        }}
      >
        <Box sx={{
          position: "relative",
          width: '100%',
          height: '100%',
          maxWidth: {
            xs: '100%',
            sm: 'calc((100dvh - 7rem) * 0.5625)', // 9/16 aspect ratio - CENTERED ON DESKTOP
            lg: 'calc((100dvh - 7rem) * 0.5625)',
          },
        }}>
          <Box
            onClick={handleVideoClick}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={(e) => {
              e.preventDefault();
              handlePressStart(e);
            }}
            onTouchEnd={handlePressEnd}
            onTouchCancel={handlePressEnd}
            sx={{
              width: '100%',
              height: '100%',
              maxWidth: {
                xs: '100%',
                sm: 'calc((100dvh - 7rem) * 0.5625)', // 9/16 aspect ratio - CENTERED ON DESKTOP
                lg: 'calc((100dvh - 7rem) * 0.5625)',
              },
              position: 'relative',
              cursor: 'pointer',
              userSelect: 'none',
              borderRadius: { xs: 0, sm: '16px' },
              overflow: 'hidden',
              bgcolor: '#000',
              aspectRatio: '9 / 16',
              margin: '0 auto', // CENTER REELS
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'translateZ(0)',
              willChange: 'transform',
            }}
          >
            <video
              ref={videoRef}
              src={video.videoUrl} // Cache buster for retries
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              playsInline
              muted={isMuted}
              loop
              preload="auto"
              onError={handleVideoError}
              onContextMenu={(e) => e.preventDefault()}
            />

            {/* Gradient Overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 20%, transparent 60%, rgba(0,0,0,0.9) 100%)',
                pointerEvents: 'none',
              }}
            />

            {/* Buffering Indicator */}
            <Fade in={buffering}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={48} sx={{ color: '#fff' }} />
              </Box>
            </Fade>

            {/* Double Tap Heart Animation */}
            <Fade in={showDoubleTapHeart}>
              <Box
                sx={{
                  position: 'absolute',
                  left: doubleTapPosition.x,
                  top: doubleTapPosition.y,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  zIndex: 40,
                  animation: 'heartBurst 0.8s ease-out',
                  '@keyframes heartBurst': {
                    '0%': {
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(0)',
                    },
                    '15%': {
                      opacity: 0.9,
                      transform: 'translate(-50%, -50%) scale(1)',
                    },
                    '30%': {
                      opacity: 1,
                      transform: 'translate(-50%, -50%) scale(1.2)',
                    },
                    '100%': {
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(1.3)',
                    },
                  },
                }}
              >
                <Favorite
                  sx={{
                    fontSize: 100,
                    color: '#fff',
                    filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.3))',
                  }}
                />
              </Box>
            </Fade>

            {/* Video Error State */}
            {/* {videoError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.8)',
                  zIndex: 30,
                }}
              >
                <ErrorOutline sx={{ fontSize: 64, color: '#fff', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                  Video Unavailable
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}
                >
                  {retryCount < MAX_RETRIES
                    ? `Retrying... (${retryCount}/${MAX_RETRIES})`
                    : 'Unable to load video'}
                </Typography>
                {retryCount >= MAX_RETRIES && (
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRetryCount(0);
                      setVideoError(false);
                      videoRef.current?.load();
                    }}
                    sx={{
                      color: '#fff',
                      borderColor: '#fff',
                      '&:hover': {
                        borderColor: '#fff',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Retry
                  </Button>
                )}
              </Box>
            )} */}

            {/* Hold Pause Indicator */}
            {/* <Fade in={isHoldingPause}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 30,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'rgba(0,0,0,0.7)',
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Pause sx={{ fontSize: 32, color: '#fff' }} />
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  Release to continue
                </Typography>
              </Box>
            </Box>
          </Fade> */}

            {/* Paused Indicator */}
            <Fade in={isPaused}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Pause sx={{ fontSize: 64, color: 'rgba(255,255,255,0.8)' }} />
              </Box>
            </Fade>

            {/* Volume Control */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 20,
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMute();
                }}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  backdropFilter: { xs: undefined, md: 'blur(8px)' },
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.6)',
                  },
                }}
              >
                {isMuted ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
            </Box>

            {/* Video Info Overlay (Mobile/Tablet) */}
            <>
              {/* {console.log(isLargeScreen , "isLargeScreen isLargeScreen")} */}
              {/* Action Buttons */}
              {(
                <ActionButtons
                  forceMobile={forceMobileActions}
                  currentIndex={currentIndex}
                  isLargeScreen={isLargeScreen}
                  onInterested={onInterested}
                  onNextReel={onNextReel}
                  onPreviousReel={onPreviousReel}
                  onUninterested={onUninterested}
                  activeButtons={activeButtonsState}
                  setActiveButtons={setActiveButtonsState}
                  isVisible={!isEndCardVisible}
                  isSelected={isSelected}
                  isActionLoading={isActionLoading}
                  onOpenDocument={handleOpenDocument}
                  hasPitchDeck={!!video?.pitch_deck_path}
                  userPermissions={userPermissions}
                  addToast={addToast}
                  onMuteVideo={onMuteVideo}
                />
              )}
            </>
            {/* UPDATED: Video metadata overlay with new format */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 0, md: 0 },
                left: { xs: 0, md: 0 },
                right: { xs: 0, md: 0 },
                paddingBottom: "1rem",
                paddingInline: "0.75rem",
                zIndex: 10,
                background: [
                  'linear-gradient(to top, transparent 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.7) 100%)',
                  '-webkit-linear-gradient(bottom, transparent 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.9) 100%)',
                  '-moz-linear-gradient(bottom, transparent 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.9) 100%)',
                ],
                height: "8vh"
              }}
            />
            <Box
  sx={{
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: { xs: '1rem', md: '1.25rem' },
    paddingInline: { xs: '0.75rem', md: '1rem' },
    zIndex: 10,
    background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.75) 40%, rgba(0,0,0,0.4) 70%, transparent 100%)',
    WebkitBackfaceVisibility: 'hidden',
    color: 'white',
    animation: 'slideUpMetadata 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '@keyframes slideUpMetadata': {
      '0%': {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
  }}
>
  {/* Header: Avatar + Startup Name + View Profile Button */}
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1, sm: 1.5 },
      mb: { xs: 0.75, md: 1 },
    }}
  >
    <Avatar
      src={video?.thumbnailUrl || video?.creator?.avatar}
      alt={video?.startupName || video?.creator?.username}
      sx={{
        width: { xs: 42, sm: 48 },
        height: { xs: 42, sm: 48 },
        border: '2px solid rgba(255, 255, 255, 0.9)',
        bgcolor: '#F9F5FF',
        borderRadius: '8px',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'transform 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          borderColor: 'rgba(255, 255, 255, 1)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        onMuteVideo();
        window.open(`/deal-flow/view-profile?startup_id=${video?.id}`, '_blank', 'noopener,noreferrer');
      }}
    />

    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
      {/* Startup Name + View Profile Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 0.5,
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onMuteVideo();
          window.open(`/deal-flow/view-profile?startup_id=${video?.id}`, '_blank', 'noopener,noreferrer');
        }}
      >
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            textShadow: '0 2px 8px rgba(0,0,0,0.9)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '0 1 auto',
            minWidth: 0,
          }}
        >
          {video?.startupName}
        </Typography>

        <Box
          component="span"
          sx={{
            flexShrink: 0,
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(8px)',
            fontSize: { xs: '0.7rem', md: '0.75rem' },
            fontWeight: 500,
            padding: { xs: '4px 8px', md: '4px 12px' },
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            borderRadius: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.25)',
              borderColor: 'rgba(255, 255, 255, 1)',
            },
            '&:active': {
              transform: 'scale(0.96)',
            },
          }}
        >
          View Profile
        </Box>
      </Box>

      {/* Stage | Category | Sector */}
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: '0.75rem', sm: '0.8rem' },
          color: 'rgba(255, 255, 255, 0.95)',
          textShadow: '0 1px 4px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}
      >
        {[video?.stage, video?.category, video?.sectors]
          .filter(Boolean)
          .join(' | ')}
      </Typography>
    </Box>
  </Box>

  {/* Description Section */}
  <Box
    ref={descriptionRef}
    sx={{
      maxHeight: isDescriptionExpanded ? '30vh' : 'auto',
      overflowY: isDescriptionExpanded ? 'auto' : 'hidden',
      transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      WebkitOverflowScrolling: 'touch',
      '&::-webkit-scrollbar': {
        width: '4px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '2px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '2px',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.5)',
        },
      },
    }}
    data-desc-box="true"
  >
    <Box
      onClick={(e) => {
        e.stopPropagation();
        setIsDescriptionExpanded(!isDescriptionExpanded);
      }}
      sx={{
        color: '#fff',
        fontSize: { xs: '0.875rem', sm: '0.9rem' },
        lineHeight: 1.5,
        textShadow: '0 2px 6px rgba(0,0,0,0.9)',
        cursor: 'pointer',
        display: isDescriptionExpanded ? 'block' : '-webkit-box',
        overflow: isDescriptionExpanded ? 'visible' : 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: isDescriptionExpanded ? 'unset' : 2,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word',
        transition: 'opacity 0.2s ease',
        '&:hover': {
          opacity: 0.9,
        },
        '& p, & div, & span, & h1, & h2, & h3, & h4, & h5, & h6': {
          display: 'inline',
          margin: 0,
          padding: 0,
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
        '& br': {
          display: isDescriptionExpanded ? 'block' : 'none',
          content: '""',
        },
        '& > *:first-of-type': {
          marginTop: 0,
        },
      }}
      dangerouslySetInnerHTML={{ __html: video?.description || '' }}
    />
  </Box>
</Box>


            {/* Progress Bar (Mobile) */}
            {(
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  zIndex: 25,
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(progress / duration) * 100 || 0}%`,
                    bgcolor: '#fff',
                    transition: 'width 0.2s ease-out linear infinite',
                    willChange: 'width',
                  }}
                />
              </Box>
            )}
          </Box>
          {!forceMobileActions && (
            <ActionButtons
              currentIndex={currentIndex}
              isLargeScreen={isLargeScreen}
              onInterested={onInterested}
              onNextReel={onNextReel}
              onPreviousReel={onPreviousReel}
              onUninterested={onUninterested}
              activeButtons={activeButtonsState}
              setActiveButtons={setActiveButtonsState}
              isVisible={!isEndCardVisible}
              isSelected={isSelected}
              isActionLoading={isActionLoading}
              onOpenDocument={handleOpenDocument}
              hasPitchDeck={!!video?.pitch_deck_path}
              userPermissions={userPermissions}
              addToast={addToast}
              onMuteVideo={onMuteVideo}
            />
          )}
        </Box>
      </Box>
    </>
  );
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: '100%',
  overflowY: 'auto',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  flex: '1 0 auto',
  borderRight: '1px solid #ebe6e2',
  [theme.breakpoints.up('md')]: {
    flex: '0 0 40%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '450px',
  },
}));

// Main Player Component - UPDATED with scroll-down behavior (requirement #4)
const VideoReelPlayer = ({ value: tabvalue }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isLargeScreen = isDesktop;
  const userPermissions = useSelector((state) => state?.permissions?.userPermissions);
  const { addToast } = useToast();

  // console.log(isLargeScreen, "isLargeScreen")
  const navigate = useNavigate();

  const investor_id = Cookies.get('X-INV-ID');

  // ── Record a view once a reel has actually been watched ────────────────────
  const { mutateAsync: SaveViewAsync } = SaveView();

  const handleReelWatched = useCallback((video) => {
    const reel_id = video?.id;
    const startup_id = video?.startupId || video?.id;
    if (!reel_id || !startup_id || !investor_id) return;
    if (!shouldRecordView(getReelLastViewedAt(investor_id, reel_id))) return;

    SaveViewAsync({ startup_id, investor_id, view_type: 'pitch_reel' })
      .then((response) => {
        if (response?.data?.investorInfo?.statusCode == 200) {
          setReelLastViewedAt(investor_id, reel_id);
        }
      })
      .catch((err) => {
        addToast(err?.response?.data?.message, 'error');
      });
  }, [investor_id, SaveViewAsync, addToast]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedStartupId, setSelectedStartupId] = useState(searchParams?.get('st'));
  const [value, setValue] = useState(tabvalue || 'deal-flow');
  const currentRoute = value || 'deal-flow';

  const {
    videos,
    total,
    hasMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    handleAction,
    isActionLoading,
    refetch: refetchAllVideo
  } = usePitchReels(
    investor_id,
    currentRoute === 'selected' ? 'selected' : currentRoute === "all" ? "outofpreference" : currentRoute,
    selectedStartupId,
    setSelectedStartupId
  );
  // console.log(investor_id, "investor_id in Video Reel Player")

  React.useEffect(() => {
    if (currentRoute === "selected") return;

    if (searchParams.has("st")) {
      const params = new URLSearchParams(searchParams);
      params.delete("st");
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, currentRoute]);

  // ===== FIX: Add useEffect to sync value with tabvalue =====
  useEffect(() => {
    if (tabvalue && tabvalue !== value) {
      setValue(tabvalue);
      setSelectedStartupId(null);
    }
  }, [tabvalue, searchParams?.get('st')]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [likedVideos, setLikedVideos] = useState(new Set());
  const [savedVideos, setSavedVideos] = useState(new Set());
  const [followedUsers, setFollowedUsers] = useState(new Set());
  const [isMuted, setIsMuted] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showEndCard, setShowEndCard] = useState(false);
  const [isEndCardVisible, setIsEndCardVisible] = useState(false);

  const [activeButtonsMap, setActiveButtonsMap] = useState({});

  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const endCardRef = useRef(null);
  const endCardObserverRef = useRef(null);

  const [openModal, setOpenModal] = React.useState(false);

  const selected_file = sessionStorage.getItem("selected-file") ? JSON.parse(sessionStorage.getItem("selected-file")) : null;

  React.useEffect(() => {
    if (searchParams.get("view-document") === "true") {
      setOpenModal(true);
    }
  }, [searchParams]);

  const handleClose = () => {
    // searchParams.delete("upgrade");
    // setSearchParams(searchParams);
    setOpenModal(false);
    searchParams.delete("view-document");
    sessionStorage.removeItem("selected-file");
    setSearchParams(searchParams, { replace: true });
  };

  const handleOpenDocument = (video) => {
    // Decks opened from the reel feed count towards the founder's Deck Views
    // exactly like one opened from the profile page. Same target, so an investor
    // who opens the same deck both ways in a day is still one view.
    trackEngagementEvent({
      startup_id: video?.startupId || video?.id,
      investor_id,
      view_type: VIEW_TYPES.DECK_VIEW,
      target: 'pitch_deck',
    });

    // Set the cookie with file information
    sessionStorage.setItem(
      "selected-file",
      JSON.stringify({
        file_name: video?.startupName ? `${video?.startupName}` : "Pitch Deck",
        startup_name: video?.startupName ? video?.startupName : "Pitch Deck",
        url: video?.pitch_deck_path,
      })
    );

    // Set search params to open the modal
    setSearchParams(prev => ({ ...Object.fromEntries(prev), 'view-document': 'true' }));
  };

  const scrollToIndex = useCallback((index) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const videoElement = container.querySelector(`[data-index="${index}"]`);
    if (videoElement) {
      // Use smoother scroll with easing
      videoElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  // Observer for videos
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Collapse description on scroll
    const handleScroll = () => {
      const videoItems = document.querySelectorAll('[data-index]');
      videoItems.forEach((item) => {
        const descBox = item.querySelector('[data-desc-box]');
        if (descBox) {
          descBox.__setExpanded?.(false);
        }
      });
    };

    container.addEventListener('scroll', handleScroll);

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
            const index = parseInt(entry.target.dataset.index);
            setCurrentIndex(index);

            if (index === videos.length - 1 && !hasNextPage && !isFetchingNextPage) {
              setShowEndCard(true);
            } else {
              setShowEndCard(false);
            }

            if (index >= videos.length - 2 && hasNextPage && !isFetchingNextPage) {
              searchParams.delete("st");
              setSearchParams(searchParams, { replace: true });
              fetchNextPage();
            }
          }
        });
      },
      {
        root: container,
        threshold: [0.75],
      }
    );

    const videoElements = container.querySelectorAll('[data-index]');
    videoElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      container.removeEventListener('scroll', handleScroll);
    };
  }, [videos.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Observer for EndOfFeedCard
  useEffect(() => {
    const endCardElement = endCardRef.current;
    if (!endCardElement || !showEndCard) {
      setIsEndCardVisible(false);
      return;
    }

    endCardObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 1.0) {
            setIsEndCardVisible(true);
          } else {
            setIsEndCardVisible(false);
          }
        });
      },
      {
        root: scrollContainerRef.current,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      }
    );

    endCardObserverRef.current.observe(endCardElement);

    return () => {
      if (endCardObserverRef.current) {
        endCardObserverRef.current.disconnect();
      }
    };
  }, [showEndCard]);

  const currentVideo = videos[currentIndex];

  const handleContinueToNextRoute = useCallback(async () => {

    const queryClient = new QueryClient()
    console.log(currentRoute, "currentRoute")

    switch (currentRoute) {
      case 'selected':
        setSearchParams({});
        navigate('/pitch-reels/deal-flow', { replace: true });
        setShowEndCard(false);
        setCurrentIndex(0);
        break;
      case 'deal-flow':
        setValue("all")
        setShowEndCard(false);
        setCurrentIndex(0);
        // navigate('/pitch-reels/all', { replace: true });
        break;
      case 'preference':
        setValue("all")
        setShowEndCard(false);
        setCurrentIndex(0);
        // navigate('/pitch-reels/all', { replace: true });
        break;
      case 'all':
        // NEW: Check if there are more pages first (existing pagination)
        if (hasNextPage && !isFetchingNextPage) {
          // Continue with normal pagination
          setShowEndCard(false);
          await fetchNextPage();

          // Scroll to the first video of the new page
          setTimeout(() => {
            scrollToIndex(videos.length);
          }, 300);
        } else {
          // No more pages in existing pagination, check for NEW videos
          setShowEndCard(false);

          try {
            const lastVideo = videos[videos.length - 1];

            // Fetch newly registered videos
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/pitch-reel/new`, {
              headers: {
                Authorization: `Bearer ${checkForAuthorization()}`,
              },
              params: {
                investor_id,
                viewType: currentRoute === 'selected' ? 'selected' :
                  currentRoute === 'all' ? 'outofpreference' : currentRoute,
                after_timestamp: lastVideo?.createdAt || new Date().toISOString(),
                limit: 10
              }
            });

            const newVideos = response.data?.data?.videos || [];

            if (newVideos.length > 0) {
              const queryKey = ['pitch-reels', investor_id, currentRoute, selectedStartupId].filter(Boolean);

              // Append new videos to the cache
              queryClient.setQueryData(queryKey, (oldData) => {
                if (!oldData) return oldData;

                const updatedPages = [...oldData.pages];
                const lastPageIndex = updatedPages.length - 1;
                const lastPage = updatedPages[lastPageIndex];

                updatedPages[lastPageIndex] = {
                  ...lastPage,
                  data: {
                    ...lastPage.data,
                    videos: [...(lastPage.data?.videos || []), ...newVideos],
                    total: (lastPage.data?.total || 0) + newVideos.length,
                    hasMore: false // These are new videos, not part of pagination
                  }
                };

                return {
                  ...oldData,
                  pages: updatedPages,
                  pageParams: oldData.pageParams
                };
              });

              addToast?.(`${newVideos.length} new reel${newVideos.length > 1 ? 's' : ''} found!`, 'success');

              setTimeout(() => {
                scrollToIndex(videos.length);
              }, 300);
            } else {
              addToast?.('No new reels available at this time', 'info');
            }
          } catch (error) {
            console.error('Failed to fetch new videos:', error);
            addToast?.('Failed to check for new videos', 'error');
          }
        }
        break;
      default:
        break;
    }
  }, [currentRoute, navigate, setSearchParams]);

  const handleGoBack = useCallback((viewType, noVideosAvailable) => {
    if (viewType === "all") {
      navigate("/")
      // setValue("deal-flow")
    }
    else if (viewType === "deal-flow" && noVideosAvailable) {
      navigate("/")
    }
    setShowEndCard(false);
  }, [navigate]);

  const handleLike = useCallback((videoId) => {
    setLikedVideos((prev) => {
      const next = new Set(prev);
      const isLiked = next.has(videoId);

      if (isLiked) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }

      return next;
    });
  }, []);

  const handleSave = useCallback((videoId) => {
    setSavedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  }, []);

  const handleFollow = useCallback((userId) => {
    setFollowedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  // UPDATED: Requirement #4 - Don't remove reel, just scroll down
  const handleInterested = useCallback(videoId => {
    const cv = videos.find(v => v.id === videoId)
    const videoIndex = videos.findIndex(v => v.id === videoId)
    const isCurrentVideo = videoIndex === currentIndex;

    setActiveButtonsMap(prev => ({ ...prev, [videoId]: { interested: true, uninterested: false } }));

    handleAction(cv.startupid || cv.id, 'like', {
      onSuccess: () => {
        // setSelectedStartupId(videoId);
        // setSearchParams(prev => ({ ...Object.fromEntries(prev), st: videoId }));

        // UPDATED: Just scroll to next reel, don't remove current one
        if (isCurrentVideo && videoIndex < videos.length - 1) {
          setTimeout(() => {
            scrollToIndex(videoIndex + 1);
          }, 300);
        } else {
          setShowEndCard(true);
          const container = scrollContainerRef.current;
          if (container) {
            const endCard = container.querySelector(`[data-index="${total + 1}"]`);
            if (endCard) {
              const containerTop = container.getBoundingClientRect().top;
              const elementTop = endCard.getBoundingClientRect().top;
              const scrollOffset = elementTop - containerTop;

              container.scrollTo({
                top: container.scrollTop + scrollOffset,
                behavior: 'smooth',
              });
            }
          }
        }
      }
    })
  }, [videos, handleAction, currentRoute, currentIndex, scrollToIndex, setSearchParams]);

  const handleUninterested = useCallback(
    (videoId) => {
      const cv = videos.find((v) => v.id === videoId);
      if (!cv) return;

      const videoIndex = videos.findIndex((v) => v.id === videoId);
      const isCurrentVideo = videoIndex === currentIndex;

      setActiveButtonsMap(prev => ({
        ...prev,
        [videoId]: { interested: false, uninterested: true }
      }));

      handleAction(cv.startupid || cv.id, 'dislike', {
        onSuccess: () => {
          // setSelectedStartupId(videoId);
          // setSearchParams(prev => ({ ...Object.fromEntries(prev), st: videoId }));

          // UPDATED: Just scroll to next reel, don't remove current one
          if (isCurrentVideo && videoIndex < videos.length - 1) {
            setTimeout(() => {
              scrollToIndex(videoIndex + 1);
            }, 300);
          } else {
            setShowEndCard(true);
            const container = scrollContainerRef.current;
            if (container) {
              const endCard = container.querySelector(`[data-index="${total + 1}"]`);
              if (endCard) {
                const containerTop = container.getBoundingClientRect().top;
                const elementTop = endCard.getBoundingClientRect().top;
                const scrollOffset = elementTop - containerTop;

                container.scrollTo({
                  top: container.scrollTop + scrollOffset,
                  behavior: 'smooth',
                });
              }
            }
          }
        },
        onError: (error) => {
          console.error('Failed to mark as uninterested', error);
        },
      });
    },
    [videos, handleAction, currentRoute, currentIndex, scrollToIndex, setSearchParams]
  );

  const handleShare = useCallback(() => {
    if (!currentVideo) return;

    if (navigator.share) {
      navigator.share({
        title: currentVideo.title,
        text: currentVideo.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  }, [currentVideo]);

  useEffect(() => {
    if (currentIndex >= videos.length && videos.length > 0) {
      const newIndex = Math.max(0, videos.length - 1);
      setCurrentIndex(newIndex);
      setTimeout(() => {
        scrollToIndex(newIndex);
      }, 50);
    }
  }, [videos.length, currentIndex, scrollToIndex]);

  // Mute video when End Card is visible, unmute when hidden
  useEffect(() => {
    if (currentIndex === videos?.length - 1) {
      if (isEndCardVisible) {
        setIsMuted(isEndCardVisible);
      } else {
        // setIsMuted(false);
      }
    }
  }, [isEndCardVisible]);

  // React.useEffect(() => {
  //   if (videos.length > 0) {
  //     setSearchParams(prev => ({ ...Object.fromEntries(prev), st: videos?.at(currentIndex).id }));
  //   } else {
  //     console.log("ShowEnd")
  //   }
  // }, [currentIndex, videos.length])

  const handlePreviousReel = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      // const video = videos[newIndex];
      // if (video) {
      //   setSelectedStartupId(video.id);
      //   setSearchParams(prev => ({ ...Object.fromEntries(prev), st: video.id }));
      // }
      scrollToIndex(newIndex);
    }
  }, [currentIndex, scrollToIndex, videos, setSearchParams]);

  const handleNextReel = useCallback(() => {
    if (currentIndex < videos.length - 1) {
      const newIndex = currentIndex + 1;
      // const video = videos[newIndex];
      // if (video) {
      //   setSelectedStartupId(video.id);
      //   setSearchParams(prev => ({ ...Object.fromEntries(prev), st: video.id }));
      // }
      scrollToIndex(newIndex);
    } else if (!hasNextPage && videos.length > 0) {
      setShowEndCard(true);
      const container = scrollContainerRef.current;
      if (container) {
        const endCard = container.querySelector(`[data-index="${total + 1}"]`);
        if (endCard) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = endCard.getBoundingClientRect().top;
          const scrollOffset = elementTop - containerTop;

          container.scrollTo({
            top: container.scrollTop + scrollOffset,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [currentIndex, videos.length, scrollToIndex, hasNextPage, total, videos, setSearchParams]);

  if (isLoading) {
    return (
      <ThemeBGWrapper>
        <BreadcrumbHeader
          sx={{ paddingBottom: '0rem !important' }}
          customBackgroundColor={isMobile ? 'black !important' : undefined}
          title="Pitch Reels"
          isDarkMode={isMobile}
        />
        <PitchReelsFeedSkeleton />
      </ThemeBGWrapper>
    );
  }

  if (isError) {
    return (
      <ThemeBGWrapper>
        <BreadcrumbHeader
          sx={{ paddingBottom: '0rem !important' }}
          customBackgroundColor={isMobile ? 'black !important' : undefined}
          title="Pitch Reels"
          isDarkMode={isMobile}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100dvh - 3.5rem)',
            gap: 2,
            bgcolor: '#000',
          }}
        >
          <Typography variant="h6" color="error">
            Failed to load pitch reels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error?.message || 'Please try again later'}
          </Typography>
        </Box>
      </ThemeBGWrapper>
    );
  }

  // if (!videos || videos.length === 0) {
  //   const getEmptyStateMessage = () => {
  //     switch (currentRoute) {
  //       case "selected":
  //         return {
  //           icon: <ErrorOutline sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />,
  //           title: "No Pitch Reels Yet",
  //           description: "This startup hasn't uploaded any pitch reels. Check back soon or explore other opportunities.",
  //           actionText: "Explore Deal Flow",
  //           secondaryAction: "View All Startups",
  //           showAction: true,
  //         };
  //       case "deal-flow":
  //         return {
  //           icon: <ErrorOutline sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />,
  //           title: "Your Deal Flow is Empty",
  //           description: "You haven't added any startups to your deal flow yet. Start exploring to find opportunities that match your investment criteria.",
  //           actionText: "Browse Preference Matches",
  //           secondaryAction: "View All Startups",
  //           showAction: true,
  //         };
  //       case "preference":
  //         return {
  //           icon: <ErrorOutline sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />,
  //           title: "No Preference Matches",
  //           description: "No startups match your current preferences. Try adjusting your filters or explore all available reels.",
  //           actionText: "View All Startups",
  //           secondaryAction: "Update Preferences",
  //           showAction: true,
  //         };
  //       case "all":
  //         return {
  //           icon: <ErrorOutline sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />,
  //           title: "No Reels Available",
  //           description: "There are no pitch reels available at the moment. Check back later for new startup pitches.",
  //           actionText: "Go Back",
  //           secondaryAction: null,
  //           showAction: true,
  //         };
  //       default:
  //         return {
  //           icon: <ErrorOutline sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.6 }} />,
  //           title: "No Content Available",
  //           description: "Check back later for new pitch reels and investment opportunities.",
  //           actionText: "Go Back",
  //           secondaryAction: null,
  //           showAction: true,
  //         };
  //     }
  //   };

  //   const emptyState = getEmptyStateMessage();

  //   return (
  //     <ThemeBGWrapper>
  //       <BreadcrumbHeader
  //         sx={{ paddingBottom: "0rem !important" }}
  //         title="Pitch Reels"
  //       />
  //       <Box
  //         sx={{
  //           display: "flex",
  //           flexDirection: "column",
  //           alignItems: "center",
  //           justifyContent: "center",
  //           height: "calc(100dvh - 6rem)",
  //           px: 3,
  //         }}
  //       >
  //         <NoData
  //           title={emptyState.title}
  //           subTitle={emptyState.description}
  //           placeHolderImage={placeHolderImageNew}
  //           sx={{
  //             maxWidth: 500
  //           }}
  //           Actions={() => {
  //             return (
  //               <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 1.5 }}>
  //                 <Primary
  //                   size="small"
  //                   onClick={handleContinueToNextRoute}
  //                   sx={{
  //                     padding: "0.75rem 1.5rem",
  //                     fontWeight: 600,
  //                     fontSize: "1rem",
  //                     textTransform: "none",
  //                   }}
  //                 >
  //                   {emptyState.actionText}
  //                 </Primary>

  //                 {emptyState.secondaryAction && (
  //                   <Secondary
  //                     size="small"
  //                     onClick={handleGoBack}
  //                     sx={{
  //                       padding: "0.75rem 1.5rem",
  //                       fontWeight: 500,
  //                       fontSize: "0.95rem",
  //                       textTransform: "none",
  //                     }}
  //                   >
  //                     {emptyState.secondaryAction}
  //                   </Secondary>
  //                 )}
  //               </Box>
  //             )
  //           }} />
  //       </Box>
  //     </ThemeBGWrapper>
  //   );
  // }

  const the_pitch = currentVideo?.description;

  return (
    <ThemeBGWrapper>
      <BreadcrumbHeader
        sx={{ paddingBottom: '0rem !important' }}
        customBackgroundColor={isMobile ? 'black !important' : undefined}
        title="Pitch Reels"
        isDarkMode={isMobile}
      />

      {Boolean(selected_file) && <CustomPopup open={openModal} handleClose={handleClose} title={<> {selected_file?.file_name} <>
        <Tooltip title="Download">
          <IconButton
            size="medium"
            sx={{
              position: 'absolute',
              top: '0.5rem',
              right: '3rem',
              // borderRadius: '4px',
              // color: theme.palette.customColors.indigo600
            }}
            onClick={(e) => {
              downloadFile(selected_file?.url, (selected_file?.startup_name || "Startup") + "_pitchdeck");
            }}
            aria-label="more options"
          >
            <DownloadIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      </> </>} titleSx={{
        marginBottom: { xs: '0rem', md: '2rem' },
        marginTop: { xs: '2rem', md: '0rem' },
      }} sx={{
        paddingTop: '0rem',
        placeContent: 'stretch',
        height: '100%'
      }} key={"File Popup"} >

        <DisplayDocuments type={selected_file?.url?.split('.')?.pop()?.toLowerCase()} url={selected_file?.url} />

      </CustomPopup>}

      {!videos || videos.length === 0 ? <>
        <Box
          sx={{
            width: '100%',
            height: {
              xs: 'calc(100dvh - 3.5rem)',
              sm: 'calc(100dvh - 7rem)',
            },
            padding: { xs: "", sm: "1rem" }
          }}
        >
          <EndOfFeedCard
            ref={endCardRef}
            currentRoute={currentRoute}
            onContinue={handleContinueToNextRoute}
            onGoBack={handleGoBack}
            hasNextPage={hasNextPage}
            noVideosAvailable={videos.length === 0}
          />
        </Box>
      </> : <>
        {/* Desktop Layout - UPDATED: Centered reels */}
        {isDesktop ? (
          <Box
            sx={{
              display: 'flex',
              height: 'calc(100dvh - 5rem)',
              gap: 3,
              pl: '1rem',
            }}
          >

            {/* Right Side - Scrollable Video Player - UPDATED: Centered */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                ref={scrollContainerRef}
                sx={{
                  width: '100%',
                  // maxWidth: 500,
                  height: '100%',
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                  scrollSnapType: 'y mandatory',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  scrollPaddingTop: '0px',
                  '&': {
                    scrollBehavior: 'smooth',
                  },
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  pb: { xs: '1rem', sm: 0 },
                  mx: 'auto',
                }}
              >
                {videos.map((video, index) => (
                  <Box key={video.id} data-index={index} >
                    <VideoItem
                      video={video}
                      isActive={index === currentIndex}
                      onLike={() => handleLike(video.id)}
                      onSave={() => handleSave(video.id)}
                      onFollow={() =>
                        handleFollow(video.creator?.id)
                      }
                      isLiked={likedVideos.has(video.id)}
                      isSaved={savedVideos.has(video.id)}
                      isFollowing={followedUsers.has(
                        video.creator?.id
                      )}
                      onOpenComments={() => setCommentsOpen(true)}
                      onOpenProfile={() => setProfileOpen(true)}
                      onShare={handleShare}
                      isMuted={isMuted}
                      onToggleMute={() => setIsMuted(!isMuted)}
                      onMuteVideo={() => setIsMuted(true)}
                      isDesktop
                      onInterested={() =>
                        handleInterested(video.id)
                      }
                      onUninterested={() =>
                        handleUninterested(video.id)
                      }
                      onPreviousReel={handlePreviousReel}
                      onNextReel={handleNextReel}
                      currentIndex={currentIndex}
                      totalVideos={videos.length}
                      isEndCardVisible={isEndCardVisible}
                      isSelected={value === "selected"}
                      isActionLoading={isActionLoading}
                      activeButtonsState={activeButtonsMap[video.id] || { interested: false, uninterested: false }}
                      setActiveButtonsState={(newState) => {
                        setActiveButtonsMap(prev => ({
                          ...prev,
                          [video.id]: typeof newState === 'function' ? newState(prev[video.id] || { interested: false, uninterested: false }) : newState
                        }));
                      }}
                      userPermissions={userPermissions}
                      addToast={addToast}
                      handleOpenDocument={() => handleOpenDocument(video)}
                      onReelWatched={handleReelWatched}
                    />
                    {/* {isLargeScreen && index === currentIndex && (
                      <ScrollButtons
                        currentIndex={currentIndex}
                        isLargeScreen={isLargeScreen}
                        onInterested={() =>
                          handleInterested(video.id)
                        }
                        onNextReel={handleNextReel}
                        onPreviousReel={handlePreviousReel}
                        onUninterested={() =>
                          handleUninterested(video.id)
                        }
                        activeButtons={activeButtonsMap[video.id] || { interested: false, uninterested: false }}
                        setActiveButtons={(newState) => {
                          setActiveButtonsMap(prev => ({
                            ...prev,
                            [video.id]: typeof newState === 'function' ? newState(prev[video.id] || { interested: false, uninterested: false }) : newState
                          }));
                        }}
                        // isVisible={!isEndCardVisible}
                        isSelected={value === "selected"}
                        isActionLoading={isActionLoading}
                      />
                    )} */ }
                  </Box>
                ))}

                {showEndCard && value !== "selected" && (
                  <Box
                    sx={{
                      width: { xs: '100%', sm: 'calc(100% - 2rem)' },
                      height: {
                        xs: 'calc(100dvh - 3.5rem)',
                        sm: 'calc(100dvh - 7rem)',
                      },
                      scrollSnapAlign: 'start',
                      scrollSnapStop: 'always',
                      px: { sm: '1rem' },
                      pb: '1rem !important',
                    }}
                    data-index={videos?.length}
                  >
                    <EndOfFeedCard
                      ref={endCardRef}
                      currentRoute={currentRoute}
                      onContinue={handleContinueToNextRoute}
                      hasNextPage={hasNextPage}
                      onGoBack={handleGoBack}
                      noVideosAvailable={videos.length === 0}
                    />
                  </Box>
                )}

                {isFetchingNextPage && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 3,
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          /* Mobile/Tablet Layout */
          <Box
            sx={{
              width: '100%',
              height: isMobile
                ? 'calc(100dvh - 3.5rem)'
                : 'calc(100dvh - 5rem)',
              position: 'relative',
              // overflow: 'hidden',
              maxWidth: isMobile ? 'auto' : "auto",
              mx: 'auto',
            }}
          >
            <Box
              ref={scrollContainerRef}
              sx={{
                width: '100%',
                height: '100%',
                overflowY: 'scroll',
                // overflowX: 'hidden',
                scrollSnapType: 'y mandatory',
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {videos.map((video, index) => (
                <Box key={video.id} data-index={index} >
                  <VideoItem
                    video={video}
                    isSelected={value === "selected"}
                    isActive={index === currentIndex}
                    onLike={() => handleLike(video.id)}
                    onSave={() => handleSave(video.id)}
                    onFollow={() => handleFollow(video.creator?.id)}
                    isLiked={likedVideos.has(video.id)}
                    isSaved={savedVideos.has(video.id)}
                    isFollowing={followedUsers.has(
                      video.creator?.id
                    )}
                    onOpenComments={() => setCommentsOpen(true)}
                    onOpenProfile={() => setProfileOpen(true)}
                    onShare={handleShare}
                    isMuted={isMuted}
                    onToggleMute={() => setIsMuted(!isMuted)}
                    onMuteVideo={() => setIsMuted(true)}
                    isDesktop={false}
                    onInterested={() =>
                      handleInterested(video.id)
                    }
                    onUninterested={() =>
                      handleUninterested(video.id)
                    }
                    onPreviousReel={handlePreviousReel}
                    onNextReel={handleNextReel}
                    currentIndex={currentIndex}
                    totalVideos={videos.length}
                    isEndCardVisible={isEndCardVisible}
                    isActionLoading={isActionLoading}
                    activeButtonsState={activeButtonsMap[video.id] || { interested: false, uninterested: false }}
                    setActiveButtonsState={(newState) => {
                      setActiveButtonsMap(prev => ({
                        ...prev,
                        [video.id]: typeof newState === 'function' ? newState(prev[video.id] || { interested: false, uninterested: false }) : newState
                      }));
                    }}
                    userPermissions={userPermissions}
                    addToast={addToast}
                    handleOpenDocument={() => handleOpenDocument(video)}
                    onReelWatched={handleReelWatched}
                  />
                </Box>
              ))}

              {showEndCard && value !== "selected" && (
                <Box
                  sx={{
                    width: '100%',
                    height: {
                      xs: 'calc(100dvh - 3.5rem)',
                      sm: 'calc(100dvh - 7rem)',
                    },
                    scrollSnapAlign: 'start',
                    scrollSnapStop: 'always',
                  }}
                  data-index={total + 1}
                >
                  <EndOfFeedCard
                    ref={endCardRef}
                    currentRoute={currentRoute}
                    onContinue={handleContinueToNextRoute}
                    onGoBack={handleGoBack}
                    hasNextPage={hasNextPage}
                    noVideosAvailable={videos.length === 0}
                  />
                </Box>
              )}

              {isFetchingNextPage && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </>}


    </ThemeBGWrapper>
  );
};

export default VideoReelPlayer;
